// UI strings for the review queue, Finnish (default) and English side by side.
// Finnish is written in full and never truncated; en must have exactly the same shape.
import type { FlagStatus } from '@valyrio/engine/types';
import { defineArea } from '../area';

type QStatus = FlagStatus | 'unsent';

export const reviewFi = {
  title: 'Tarkistusjono',
  status: { open: 'avoin', handled: 'käsitelty sessiossa', closed: 'suljettu', unsent: 'lähettämättä' } as Record<QStatus, string>,
  filterStatus: 'Tila',
  filterKind: 'Laji',
  all: 'Kaikki',
  allKinds: 'Kaikki lajit',
  statusPlural: { open: 'Avoimet', handled: 'Käsitellyt', closed: 'Suljetut', unsent: 'Lähettämättä' } as Record<QStatus, string>,
  empty: 'Ei merkintöjä tällä rajauksella.',
  emptyAll: 'Jono on tyhjä. Voit merkitä minkä tahansa sanan, merkityksen tai muodon tarkistettavaksi siellä, missä näet sen.',
  messages: (n: number) => (n === 1 ? '1 viesti' : `${n} viestiä`),
  pendingAction: 'Toiminto lähtee, kun yhteys palaa.',
  pick: 'Valitse merkintä listasta.',
  /** What a flag is about, in the list and the detail (review/model.ts targetLabel). */
  label: {
    sense: (n: number, l: string) => `${n}. merkitys (${l})`,
    lesson: (n: number) => `Oppitunti ${n}`,
    letter: 'Kirje',
    grammar: 'Kielioppi',
  },
  notFound: 'Merkintää ei löytynyt.',
  kind: 'Laji',
  note: 'Huomautus',
  suggested: 'Ehdotus',
  target: 'Kohde',
  table: 'Tyyppi',
  what: 'Mikä',
  id: 'Tunnus',
  screen: 'Näkymä',
  context: 'Mitä merkitsijä näki',
  openEntry: 'Avaa sanakirjassa',
  createdBy: (name: string, date: string) => `Merkitsi ${name} · ${date}`,
  resolution: 'Tarkistussession ratkaisu',
  resolvedAt: (date: string) => `käsitelty ${date}`,
  noResolution: 'Ei vielä käsitelty tarkistussessiossa.',
  closedBy: (name: string, date: string) => `Sulki ${name} · ${date}`,
  thread: 'Keskustelu',
  replyLabel: 'Vastaus',
  replyPlaceholder: 'esim. tämä ei vielä riitä, koska …',
  reply: 'Vastaa',
  replyReopens: 'Vastaa ja avaa uudelleen',
  replyReopensNote: 'Vastaus käsiteltyyn merkintään palauttaa sen avoimeksi seuraavaa sessiota varten.',
  close: 'Sulje merkintä',
  closeNote: 'Sulje, kun asia on teidän mielestänne kunnossa.',
  reopen: 'Avaa uudelleen',
  reopenText: 'Miksi avaat sen uudelleen? (valinnainen)',
  textRequired: 'Kirjoita vastaus ensin.',
  unsentNote: 'Merkintä on tallennettu tälle laitteelle ja lähtee, kun yhteys palaa.',
  tables: {
    lemma: 'sana',
    form: 'taivutusmuoto',
    gloss: 'merkitys',
    example: 'esimerkki',
    dialogue: 'repliikki',
    lesson: 'oppitunti',
    translator: 'kääntäjä',
    proposal: 'oma sana',
    app: 'sovellus',
  } as Record<string, string>,
  dead: 'Palvelin hylkäsi',
  deadIntro: 'Palvelin hylkäsi nämä muutokset pysyvästi, esimerkiksi virheellisen tiedon vuoksi. Niitä ei lähetetä uudelleen.',
  deadError: (e: string) => `Virhe: ${e}`,
  deadDismiss: 'Poista listalta',
  /** Every outbox write kind (queueWrite's last argument), as a noun in "Palvelin ei hyväksynyt muutosta (…)". */
  writeKinds: {
    flag: 'merkintä',
    letter: 'kirje',
    proposal: 'oma sana',
    gloss: 'merkitys',
    me: 'asetus',
    pace: 'tahti',
    stack: 'lausepino',
    notebook: 'vihkotehtävä',
    'sentence-card': 'lausekortti',
    'translator-case': 'kääntäjän korjausehdotus',
    usage: 'kääntäjän käyttöloki',
    'shared-result': 'yhteisen session tulos',
    roleplay: 'roolipeli',
    'show-clip-verdict': 'näyttelijän repliikin kohta',
    'show-clip-note': 'huomio näyttelijän ääntämyksestä',
    recording: 'äänitteen poisto tai palautus',
    'recording-edit': 'äänitteen leikkaus',
    'recording-gain': 'äänitteen äänenvoimakkuus',
    'recording-note': 'huomio äänitteeseen',
    'recording-mark': 'kumppanin huomio äänitteessä',
    'form-recording': 'taivutusmuodon äänite',
    'recording-take': 'äänite',
    'show-clip-tag': 'merkintä sarjan leikkeeseen',
  } as Record<string, string>,
  /** Writes the server refused, shown where they were made (lib/rejected.ts). */
  rejected: {
    lead: (what: string) => `Palvelin ei hyväksynyt muutosta (${what}).`,
    dismiss: 'Selvä',
    conflict: 'Kumppanisi muutti samaa asiaa samaan aikaan. Katso nykyinen tilanne ja tee muutos uudelleen.',
    forbidden: 'Tätä muutosta ei voi tehdä sinun tililtäsi.',
    notFound: 'Kohdetta ei enää ole. Se on ehkä poistettu.',
    generic: 'Muutos ei kelvannut palvelimelle.',
    withMessage: (m: string) => `Muutos ei kelvannut palvelimelle (${m}).`,
    reasons: {
      pos_missing: 'Sanaluokka puuttuu. Valitse sanaluokka, niin voit hyväksyä sanan.',
      letter_changed: 'Korjaus ei enää osu tekstiin, koska kirjoittaja muutti kirjettä. Valitse kohta uudelleen ja korjaa se.',
      flag_closed: 'Merkintä on jo suljettu. Avaa se uudelleen, jos asia jatkuu.',
      not_author: 'Vain kirjoittaja voi muokata kirjettä.',
      own_letter: 'Omaa kirjettä ei korjata itse. Korjaukset tekee kumppanisi.',
      empty_correction: 'Korjaus ei muuttanut mitään.',
      duplicate_headword: 'Sana on jo sanakirjassa virallisena hakusanana.',
      duplicate_own_word: 'Teillä on jo oma sana, joka kirjoitetaan samoin.',
      already_decided: 'Tämä on jo ratkaistu.',
      concurrent: 'Kumppanisi muutti samaa asiaa samaan aikaan. Katso nykyinen tilanne ja tee muutos uudelleen.',
      not_sunday: 'Aloituspäivän on oltava sunnuntai.',
      not_saturday: 'Aloituspäivän on oltava lauantai.',
      admin_only: 'Vain ylläpitäjä voi tehdä tämän.',
      // the week's letter (api routes/letters.ts, 2026-10-13)
      letter_locked: 'Kirjoita ensin oma tämän viikon kirjeesi. Sen jälkeen voit korjata kumppanisi kirjettä.',
    } as Record<string, string>,
  },
  /** Labels for what the flagger saw (flag context keys); unknown keys are shown as they are. */
  contextKeys: {
    letterId: 'Kirje',
    span: 'Kohta tekstissä',
    original: 'Alkuperäinen',
    replacement: 'Korjaus',
    hv: 'Sana',
    gloss: 'Merkitys',
    sense: 'Merkityksen numero',
    status: 'Tila',
    pos: 'Sanaluokka',
    origin: 'Alkuperä',
    fi: 'Suomeksi',
    en: 'Englanniksi',
    form: 'Muoto',
    example: 'Esimerkki',
    lemmaId: 'Hakusana',
    scene: 'Kohtaus',
    template: 'Taivutusmalli',
    params: 'Mallin tiedot',
    confident: 'Sauma varma',
    word: 'Sana',
    sentence: 'Lause',
    lessonId: 'Oppitunti',
    order: 'Oppitunnin numero',
  } as Record<string, string>,
  screens: {
    'dictionary/entry': 'Sanakirja: sana',
    'dictionary/meaning': 'Sanakirja: merkitys',
    'dictionary/example': 'Sanakirja: esimerkki',
    'dictionary/inflection': 'Sanakirja: taivutustaulukko',
    'dictionary/form': 'Sanakirja: taivutusmuoto',
    letters: 'Kirjeet',
    'own-words': 'Omat sanat',
    'translator/token': 'Kääntäjä',
    settings: 'Asetukset',
    'study/hv-native': 'Päivän kortti',
    'study/native-hv': 'Päivän kortti',
    'study/form-drill': 'Päivän kortti',
  } as Record<string, string>,
  openLetter: 'Avaa kirje',
  yes: 'kyllä',
  no: 'ei',
};

export const reviewEn: typeof reviewFi = {
  title: 'Review queue',
  status: { open: 'open', handled: 'handled in session', closed: 'closed', unsent: 'not sent' },
  filterStatus: 'Status',
  filterKind: 'Kind',
  all: 'All',
  allKinds: 'All kinds',
  statusPlural: { open: 'Open', handled: 'Handled', closed: 'Closed', unsent: 'Not sent' },
  empty: 'No flags with this filter.',
  emptyAll: 'The queue is empty. Flag any word, meaning or form right where you see it.',
  messages: (n) => (n === 1 ? '1 message' : `${n} messages`),
  pendingAction: 'The action sends when you are back online.',
  pick: 'Pick a flag from the list.',
  label: {
    sense: (n, l) => `meaning ${n} (${l})`,
    lesson: (n) => `Lesson ${n}`,
    letter: 'Letter',
    grammar: 'Grammar',
  },
  notFound: 'Flag not found.',
  kind: 'Kind',
  note: 'Note',
  suggested: 'Suggestion',
  target: 'Target',
  table: 'Type',
  what: 'What',
  id: 'Id',
  screen: 'Screen',
  context: 'What the flagger saw',
  openEntry: 'Open in the dictionary',
  createdBy: (name, date) => `Flagged by ${name} · ${date}`,
  resolution: 'Review session’s resolution',
  resolvedAt: (date) => `handled ${date}`,
  noResolution: 'Not handled in a review session yet.',
  closedBy: (name, date) => `Closed by ${name} · ${date}`,
  thread: 'Thread',
  replyLabel: 'Reply',
  replyPlaceholder: 'e.g. this is not enough yet, because …',
  reply: 'Reply',
  replyReopens: 'Reply and reopen',
  replyReopensNote: 'A reply to a handled flag makes it open again for the next session.',
  close: 'Close flag',
  closeNote: 'Close it when you both think it is settled.',
  reopen: 'Reopen',
  reopenText: 'Why reopen it? (optional)',
  textRequired: 'Write the reply first.',
  unsentNote: 'The flag is saved on this device and sends when you are back online.',
  tables: {
    lemma: 'word',
    form: 'inflected form',
    gloss: 'meaning',
    example: 'example',
    dialogue: 'dialogue line',
    lesson: 'lesson',
    translator: 'translator',
    proposal: 'own word',
    app: 'app',
  },
  dead: 'Rejected by the server',
  deadIntro: 'The server rejected these changes for good (e.g. invalid data). They will not be sent again.',
  deadError: (e) => `Error: ${e}`,
  deadDismiss: 'Remove from list',
  writeKinds: {
    flag: 'flag',
    letter: 'letter',
    proposal: 'own word',
    gloss: 'meaning',
    me: 'setting',
    pace: 'pace',
    stack: 'sentence stack',
    notebook: 'notebook task',
    'sentence-card': 'sentence card',
    'translator-case': 'translator correction',
    usage: 'translator usage log',
    'shared-result': 'shared session result',
    roleplay: 'role play',
    'show-clip-verdict': "the actor's clip placement",
    'show-clip-note': "note on the actor's pronunciation",
    recording: 'deleting or restoring a recording',
    'recording-edit': 'trimming a recording',
    'recording-gain': 'recording volume',
    'recording-note': 'note on a recording',
    'recording-mark': "partner's note on a recording",
    'form-recording': 'recording of a form',
    'recording-take': 'recording',
    'show-clip-tag': 'tag on a show clip',
  },
  rejected: {
    lead: (what) => `The server did not accept this change (${what}).`,
    dismiss: 'OK',
    conflict: 'Your partner changed the same thing at the same time. Look at it as it is now and make the change again.',
    forbidden: 'This change cannot be made from your account.',
    notFound: 'It is not there any more. It may have been deleted.',
    generic: 'The server did not accept the change.',
    withMessage: (m) => `The server did not accept the change (${m}).`,
    reasons: {
      pos_missing: 'The word class is missing. Choose one, then you can approve the word.',
      letter_changed: 'The correction no longer fits the text: the writer changed the letter. Select the place again and correct it.',
      flag_closed: 'The flag is already closed. Reopen it if the matter goes on.',
      not_author: 'Only the writer can edit a letter.',
      own_letter: 'You do not correct your own letter; your partner does.',
      empty_correction: 'The correction did not change anything.',
      duplicate_headword: 'The word is already an official dictionary headword.',
      duplicate_own_word: 'You already have an own word spelt the same way.',
      already_decided: 'This has already been decided.',
      concurrent: 'Your partner changed the same thing at the same time. Look at it as it is now and make the change again.',
      not_sunday: 'The start date must be a Sunday.',
      not_saturday: 'The start date must be a Saturday.',
      admin_only: 'Only the admin can do this.',
      letter_locked: 'Write your own letter for this week first. Then you can correct your partner’s.',
    },
  },
  contextKeys: {
    letterId: 'Letter',
    span: 'Place in the text',
    original: 'Original',
    replacement: 'Correction',
    hv: 'Word',
    gloss: 'Meaning',
    sense: 'Sense number',
    status: 'Status',
    pos: 'Word class',
    origin: 'Origin',
    fi: 'Finnish',
    en: 'English',
    form: 'Form',
    example: 'Example',
    lemmaId: 'Headword',
    scene: 'Scene',
    template: 'Inflection model',
    params: 'Model details',
    confident: 'Seam certain',
    word: 'Word',
    sentence: 'Sentence',
    lessonId: 'Lesson',
    order: 'Lesson number',
  },
  screens: {
    'dictionary/entry': 'Dictionary: word',
    'dictionary/meaning': 'Dictionary: meaning',
    'dictionary/example': 'Dictionary: example',
    'dictionary/inflection': 'Dictionary: inflection table',
    'dictionary/form': 'Dictionary: inflected form',
    letters: 'Letters',
    'own-words': 'Own words',
    'translator/token': 'Translator',
    settings: 'Settings',
    'study/hv-native': 'Daily card',
    'study/native-hv': 'Daily card',
    'study/form-drill': 'Daily card',
  },
  openLetter: 'Open the letter',
  yes: 'yes',
  no: 'no',
};

// Messages.review (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('review', reviewFi, reviewEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof reviewFi> {}
}
